import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { take } from 'rxjs';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { StateV4Service } from '../../../../zentility/_shared/_services/v4/state-v4.service';

interface LenConflict {
  kind: 'len-name';
  groupId: string;
  extracted: string;
  parent: string;
}

interface SaConflict {
  kind: 'sa-address';
  groupId: string;
  extracted: { street1: string; street2: string; city: string; state: string; zip: string };
  parent: { street1: string; street2: string; city: string; state: string; zip: string };
  /** True when only the state differs — gates the hard-stop UX. */
  stateMismatch: boolean;
}

type Conflict = LenConflict | SaConflict;

interface LenDraft { name: string; }
interface SaDraft { street1: string; street2: string; city: string; state: string; zip: string; }
type Draft = LenDraft | SaDraft;

@Component({
  selector: 'app-parent-conflict-resolver',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatTooltipModule],
  templateUrl: './parent-conflict-resolver.component.html',
  styleUrl: './parent-conflict-resolver.component.scss',
})
export class ParentConflictResolverComponent {
  readonly state = inject(BillAiFlowStore);
  private readonly stateV4Svc = inject(StateV4Service);

  readonly conflicts = computed<Conflict[]>(() => this.detectConflicts());

  /** State codes for the (disabled) display select — loaded from StateV4Service
   *  for consistency with the LEN/SA steps. */
  stateOptions: string[] = [];

  constructor() {
    this.stateV4Svc.getAllCountryStates().pipe(take(1)).subscribe({
      next: states => this.stateOptions = states.map(s => s.key),
    });
  }

  /** groupId → ephemeral draft used by the inline editor. Persists during typing.
   *  Shape is keyed by the conflict's `kind` — callers narrow via getDraft helpers. */
  private drafts = signal<Record<string, Draft>>({});

  /** groupId currently in Use-Existing confirmation. */
  private confirmingUseExistingFor = signal<string | null>(null);

  /** groupId + status for Change-Existing modal. */
  private changeExistingState = signal<{ groupId: string | null; status: 'idle' | 'pending' | 'error'; error?: string }>(
    { groupId: null, status: 'idle' }
  );

  getDraft(c: LenConflict): LenDraft;
  getDraft(c: SaConflict): SaDraft;
  getDraft(c: Conflict): Draft {
    const existing = this.drafts()[c.groupId];
    if (existing) return existing;
    return c.kind === 'len-name' ? { name: c.extracted } : { ...c.extracted };
  }

  /** Pre-computed drafts for template binding (avoids method calls in the template). */
  readonly lenDraftMap = computed<Record<string, LenDraft>>(() => {
    const drafts = this.drafts();
    const map: Record<string, LenDraft> = {};
    for (const c of this.conflicts()) {
      if (c.kind !== 'len-name') continue;
      map[c.groupId] = (drafts[c.groupId] as LenDraft | undefined) ?? { name: c.extracted };
    }
    return map;
  });

  readonly saDraftMap = computed<Record<string, SaDraft>>(() => {
    const drafts = this.drafts();
    const map: Record<string, SaDraft> = {};
    for (const c of this.conflicts()) {
      if (c.kind !== 'sa-address') continue;
      map[c.groupId] = (drafts[c.groupId] as SaDraft | undefined) ?? { ...c.extracted };
    }
    return map;
  });

  onLenNameInput(groupId: string, value: string): void {
    this.drafts.update(d => ({ ...d, [groupId]: { name: value } as LenDraft }));
    this.state.updateLenName(groupId, value);
  }

  onSaFieldInput(groupId: string, field: 'street1' | 'street2' | 'city' | 'zip', value: string): void {
    // Seed the draft from the conflict's extracted values, not empty strings —
    // otherwise the first edit clobbers every untouched field with '' and the
    // Change Existing PUT would wipe them on the existing SA.
    const base = this.saDraftMap()[groupId] ?? { street1: '', street2: '', city: '', state: '', zip: '' };
    this.drafts.update(d => ({ ...d, [groupId]: { ...base, [field]: value } }));
    this.state.updateSaEdit(groupId, { [field]: value });
  }

  // ===========================================================================
  // Use Existing — local rewrite of the imported entity to match the parent.
  // ===========================================================================

  isConfirmingUseExisting(groupId: string): boolean {
    return this.confirmingUseExistingFor() === groupId;
  }

  startUseExisting(groupId: string): void {
    this.confirmingUseExistingFor.set(groupId);
  }

  cancelUseExisting(): void {
    this.confirmingUseExistingFor.set(null);
  }

  confirmUseExisting(c: Conflict): void {
    if (c.kind === 'len-name') {
      this.state.updateLenName(c.groupId, c.parent);
    } else {
      this.state.applyParentSaToImported(c.groupId, c.parent);
    }
    this.confirmingUseExistingFor.set(null);
    this.drafts.update(d => { const copy = { ...d }; delete copy[c.groupId]; return copy; });
  }

  // ===========================================================================
  // Change Existing — destructive PUT against the portfolio entity.
  // ===========================================================================

  isChangeExistingActive(groupId: string): boolean {
    return this.changeExistingState().groupId === groupId;
  }

  changeExistingStatus(): 'idle' | 'pending' | 'error' {
    return this.changeExistingState().status;
  }

  changeExistingError(): string | undefined {
    return this.changeExistingState().error;
  }

  startChangeExisting(groupId: string): void {
    this.changeExistingState.set({ groupId, status: 'idle' });
  }

  cancelChangeExisting(): void {
    if (this.changeExistingState().status === 'pending') return;
    this.changeExistingState.set({ groupId: null, status: 'idle' });
  }

  async confirmChangeExisting(c: Conflict): Promise<void> {
    this.changeExistingState.update(s => ({ ...s, status: 'pending', error: undefined }));
    try {
      if (c.kind === 'len-name') {
        await this.state.commitParentLenChange(c.groupId, this.getDraft(c).name);
      } else {
        const draft = this.getDraft(c);
        await this.state.commitParentSaChange(c.groupId, {
          street1: draft.street1,
          street2: draft.street2,
          city: draft.city,
          state: c.parent.state, // enforce immutable state from parent
          zip: draft.zip,
        });
      }
      this.changeExistingState.set({ groupId: null, status: 'idle' });
      this.drafts.update(d => { const copy = { ...d }; delete copy[c.groupId]; return copy; });
    } catch (e) {
      const msg = (e as any)?.error?.message || (e as any)?.message || String(e);
      this.changeExistingState.update(s => ({ ...s, status: 'error', error: msg }));
    }
  }

  private detectConflicts(): Conflict[] {
    const ctx = this.state.getContext();
    const request = this.state.matchRequest();
    if (!ctx || !request) return [];

    // Track signals so the computed re-runs on edits.
    const overrides = this.state.entityNameOverrides();
    const saEdits = this.state.saEdits();

    const conflicts: Conflict[] = [];
    const norm = (s: string | undefined | null) => (s || '').trim().toLowerCase();

    // LEN name conflicts at SERVICE_ADDRESS / METER level
    if (ctx.lenName && (ctx.level === 'SERVICE_ADDRESS' || ctx.level === 'METER')) {
      for (const len of request.legalEntities) {
        const extractedName = overrides[len.groupId] ?? len.name ?? '';
        if (extractedName
            && norm(extractedName) !== norm(ctx.lenName)
            && norm(extractedName) !== 'unknown entity'
            && norm(extractedName) !== 'unknown') {
          conflicts.push({
            kind: 'len-name',
            groupId: len.groupId,
            extracted: extractedName,
            parent: ctx.lenName,
          });
        }
      }
    }

    // SA address conflicts at SERVICE_ADDRESS / METER level
    if (ctx.serviceAddress && (ctx.level === 'SERVICE_ADDRESS' || ctx.level === 'METER')) {
      const parent = {
        street1: ctx.serviceAddress.street1 ?? '',
        street2: ctx.serviceAddress.street2 ?? '',
        city: ctx.serviceAddress.city ?? '',
        state: ctx.serviceAddress.state ?? '',
        zip: ctx.serviceAddress.zip ?? '',
      };
      for (const len of request.legalEntities) {
        for (const sa of len.serviceAddresses) {
          const edit = saEdits[sa.groupId];
          const extracted = {
            street1: edit?.street1 ?? sa.street1 ?? '',
            street2: edit?.street2 ?? sa.street2 ?? '',
            city: edit?.city ?? sa.city ?? '',
            state: edit?.state ?? sa.state ?? '',
            zip: edit?.zip ?? sa.zip ?? '',
          };
          const same =
            norm(extracted.street1) === norm(parent.street1) &&
            norm(extracted.street2) === norm(parent.street2) &&
            norm(extracted.city) === norm(parent.city) &&
            norm(extracted.state) === norm(parent.state) &&
            norm(extracted.zip) === norm(parent.zip);
          if (same) continue;
          // Empty extracted state = not parsed off the bill, not a conflict.
          const stateMismatch = !!norm(extracted.state) && norm(extracted.state) !== norm(parent.state);
          conflicts.push({
            kind: 'sa-address',
            groupId: sa.groupId,
            extracted,
            parent,
            stateMismatch,
          });
        }
      }
    }

    return conflicts;
  }
}
